// src/pages/TurboRebuild.tsx
import { Link } from 'react-router-dom';
import { Clock, ArrowRight, Settings, BarChart4, Shield } from 'lucide-react';

const TurboRebuild = () => {
  const benefits = [
    {
      icon: <Clock className="text-autoBlue text-4xl mb-4" />,
      title: 'Hitra obnova v 2 dneh',
      description: 'Vaš turbo polnilnik bo obnovljen v najkrajšem možnem času, običajno v roku 2 delovnih dni.'
    },
    {
      icon: <Shield className="text-autoTeal text-4xl mb-4" />,
      title: '12-mesečna garancija',
      description: 'Za vse obnovljene turbo polnilnike nudimo 12-mesečno garancijo za brezskrbno vožnjo.'
    },
    {
      icon: <Settings className="text-autoOrange text-4xl mb-4" />,
      title: 'Nov in obnovljen zalog',
      description: 'Na zalogi imamo širok izbor novih in obnovljenih turbo polnilnikov za takojšnjo vgradnjo.'
    },
    {
      icon: <BarChart4 className="text-autoPurple text-4xl mb-4" />,
      title: 'Popravilo električnih aktuatorjev',
      description: 'Specializirani smo tudi za popravilo električnih aktuatorjev na turbo polnilnikih.'
    }
  ];

  const steps = [
    {
      number: '01',
      title: 'Pregled',
      description: 'Temeljit pregled trenutnega stanja turbo polnilnika in odkrivanje vzroka okvare.'
    },
    {
      number: '02',
      title: 'Razstavitev',
      description: 'Strokovna razstavitev turbo polnilnika na posamezne komponente.'
    },
    {
      number: '03',
      title: 'Čiščenje',
      description: 'Temeljito čiščenje vseh komponent z uporabo najsodobnejših tehnik.'
    },
    {
      number: '04',
      title: 'Zamenjava delov',
      description: 'Zamenjava obrabljenih ali poškodovanih delov z novimi kakovostnimi deli.'
    },
    {
      number: '05',
      title: 'Sestavljanje',
      description: 'Natančno sestavljanje vseh komponent po strogih proizvodnih standardih.'
    },
    {
      number: '06',
      title: 'Testiranje',
      description: 'Testiranje delovanja obnovljenega turbo polnilnika pred vgradnjo v vozilo.'
    }
  ];

  return (
    <div className="bg-gray-50">
      {/* Hero Section */}
      <section className="page-hero">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: "url('/assets/images/obnova-turbo-polnilnikov/pregled-testiranje.jpg')" }}
        ></div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <div className="hero-content max-w-2xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Obnova turbo polnilnikov</h1>
            <p className="text-xl mb-8">Specializirani smo za hitro in kakovostno obnovo turbo polnilnikov za vse vrste vozil. Ponujamo 12-mesečno garancijo na vse obnovljene turbo polnilnike.</p>
            <Link to="/kontakt" className="btn-primary inline-flex items-center group">
              Povprašaj za obnovo
              <ArrowRight className="ml-2 group-hover:ml-3 transition-all duration-300" size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="section-title text-center mb-12 after:left-1/2 after:-translate-x-1/2">Prednosti naše storitve</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <div 
                key={index} 
                className="bg-white rounded-xl shadow-md p-6 hover:shadow-[0_0_15px_rgba(59,122,217,0.6)] hover:border-autoBlue transition-all duration-300 text-center group border border-gray-100 card-glow"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="flex justify-center transition-transform duration-300 group-hover:scale-110">
                  {benefit.icon}
                </div>
                <h3 className="text-xl font-bold mb-2">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 bg-autoGray">
        <div className="container mx-auto px-4">
          <h2 className="section-title text-center mb-12 after:left-1/2 after:-translate-x-1/2">Naš proces obnove</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {steps.map((step, index) => (
              <div 
                key={index} 
                className="bg-white rounded-xl shadow-md p-6 hover:shadow-[0_0_15px_rgba(59,122,217,0.6)] hover:border-autoBlue transition-all duration-300 relative overflow-hidden group border border-gray-100 card-glow"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <span className="absolute top-2 left-0 right-0 text-center text-8xl font-bold text-gray-100 transition-all duration-300 group-hover:text-autoBlue/10">{step.number}</span>
                <div className="relative mt-8">
                  <h3 className="text-xl font-bold mb-2 group-hover:text-autoBlue transition-colors duration-300">{step.title}</h3>
                  <p className="text-gray-600">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Before/After Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="section-title text-center mb-12 after:left-1/2 after:-translate-x-1/2">Pred in po obnovi</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="relative transition-all duration-300 hover:shadow-[0_0_15px_rgba(59,122,217,0.6)] hover:border-autoBlue border border-gray-100 card-glow rounded-xl">
              <img 
                src="https://images.unsplash.com/photo-1578848583095-d45a4d7a4ea1?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80" 
                alt="Pred obnovo" 
                className="rounded-xl shadow-md w-full h-auto"
              />
              <div className="absolute top-4 left-4 bg-autoRed text-white px-4 py-2 rounded-full font-bold animate-pulse">PRED</div>
            </div>
            <div className="relative transition-all duration-300 hover:shadow-[0_0_15px_rgba(59,122,217,0.6)] hover:border-autoBlue border border-gray-100 card-glow rounded-xl">
              <img 
                src="https://images.unsplash.com/photo-1580806264689-25656efeffb4?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80" 
                alt="Po obnovi" 
                className="rounded-xl shadow-md w-full h-auto"
              />
              <div className="absolute top-4 left-4 bg-autoTeal text-white px-4 py-2 rounded-full font-bold animate-pulse">PO</div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-autoGray">
        <div className="container mx-auto px-4">
          <h2 className="section-title text-center mb-12 after:left-1/2 after:-translate-x-1/2">Pogosta vprašanja</h2>
          <div className="max-w-3xl mx-auto space-y-6">
            <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-[0_0_15px_rgba(59,122,217,0.6)] hover:border-autoBlue transition-all duration-300 border border-gray-100 card-glow">
              <h3 className="text-xl font-bold mb-2">Kako dolgo traja obnova turbo polnilnika?</h3>
              <p className="text-gray-600">Običajno traja obnova 1-2 delovna dneva, odvisno od zahtevnosti dela in razpoložljivosti nadomestnih delov.</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-[0_0_15px_rgba(59,122,217,0.6)] hover:border-autoBlue transition-all duration-300 border border-gray-100 card-glow">
              <h3 className="text-xl font-bold mb-2">Ali lahko obnovite vse vrste turbo polnilnikov?</h3>
              <p className="text-gray-600">Da, specializirani smo za obnovo vseh vrst turbo polnilnikov za osebna in dostavna vozila, ne glede na znamko ali model.</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-[0_0_15px_rgba(59,122,217,0.6)] hover:border-autoBlue transition-all duration-300 border border-gray-100 card-glow">
              <h3 className="text-xl font-bold mb-2">Kakšna je garancija na obnovljene turbo polnilnike?</h3>
              <p className="text-gray-600">Na vse obnovljene turbo polnilnike nudimo 12-mesečno garancijo, ki pokriva morebitne napake v materialu ali izdelavi.</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-[0_0_15px_rgba(59,122,217,0.6)] hover:border-autoBlue transition-all duration-300 border border-gray-100 card-glow">
              <h3 className="text-xl font-bold mb-2">Ali je obnova turbo polnilnika boljša izbira kot nakup novega?</h3>
              <p className="text-gray-600">Obnova je običajno stroškovno učinkovitejša rešitev, ki ohrani enako kakovost in zanesljivost kot nov turbo polnilnik, hkrati pa je tudi okolju prijaznejša izbira.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-autoBlue to-autoPurple text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Potrebujete obnovo turbo polnilnika?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">Kontaktirajte nas za brezplačno oceno in svetovanje. Naši strokovnjaki vam bodo z veseljem pomagali.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:041273738" className="bg-white text-autoBlue font-medium py-3 px-6 rounded-full hover:bg-gray-100 transition-all duration-300 inline-flex items-center justify-center shadow-lg hover:shadow-xl transform hover:-translate-y-1">
              Pokličite nas: 041 273 738
            </a>
            <Link to="/kontakt" className="border-2 border-white text-white font-medium py-3 px-6 rounded-full hover:bg-white hover:text-autoBlue transition-all duration-300 inline-flex items-center justify-center shadow-lg hover:shadow-xl transform hover:-translate-y-1 group">
              Pošljite povpraševanje
              <ArrowRight className="ml-2 group-hover:ml-3 transition-all duration-300" size={20} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TurboRebuild;